import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ClipboardList,
  Download,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
  ScanSearch,
  Send,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientWorkPickerList } from "@/components/ClientWorkPickerDialog";
import { despachoFiscalInfoApi } from "@/modules/despacho/despachoFiscalInfoApi";
import { fiscalAssignmentBoardApi } from "@/modules/despacho/despachoFiscalAssignmentApi";
import { despachoStaffApi } from "@/modules/despacho/despachoStaffApi";
import { useEntitlements } from "@/modules/useEntitlements";
import {
  fetchMaterialidadFaltantes,
  linkMaterialidadTask,
  materialidadFaltantesPdfUrl,
  materialidadCartaClientePdfUrl,
  materialidadCartaClienteXlsxUrl,
  patchMaterialidadItem,
  syncMaterialidadCfdi,
  syncMaterialidadCfdiBatch,
  uploadMaterialidadEvidence,
} from "@/modules/tramites/materialidad/materialidadApi";
import ErrorBoundary from "@/components/ErrorBoundary";
import MaterialidadContrapartesPanel from "@/modules/tramites/materialidad/MaterialidadContrapartesPanel";
import MaterialidadCartasRedaccionPanel from "@/modules/tramites/materialidad/MaterialidadCartasRedaccionPanel";
import MaterialidadExpedienteDrawer from "@/modules/tramites/materialidad/MaterialidadExpedienteDrawer";
import MaterialidadPeriodFilter from "@/modules/tramites/materialidad/MaterialidadPeriodFilter";
import {
  AUX_FILTER_ALL,
  UNASSIGNED_AUX_ID,
  buildClientAuxiliaresMap,
  filterAuxiliarGroups,
  groupFaltantesByAuxiliar,
  listAssignableStaff,
  sortCompaniesByActivity,
  staffRoleLabel,
} from "@/modules/tramites/materialidad/materialidadAuxiliarGrouping";
import {
  CRITICAL_BADGE_CLASS,
  DETECTION_LABELS,
  ITEM_STATUS_BADGE_CLASS,
  ITEM_STATUS_LABELS,
  MATERIALIDAD_TAB_FALTANTES_LABEL,
  RISK_BADGE_CLASS,
  RISK_LABELS,
  buildPeriodQuery,
  defaultPeriodSelection,
  formatPeriodLabel,
  formatPeriodSelectionLabel,
} from "@/modules/tramites/materialidad/materialidadConstants";

function StatusBadge({ status }) {
  const label = ITEM_STATUS_LABELS[status] || status;
  const cls = ITEM_STATUS_BADGE_CLASS[status] || ITEM_STATUS_BADGE_CLASS.pending;
  return (
    <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function RiskBadge({ level, critical }) {
  if (critical) {
    return (
      <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-semibold ${CRITICAL_BADGE_CLASS}`}>
        Crítico
      </span>
    );
  }
  if (!level) return <span className="text-xs text-slate-400">—</span>;
  const label = RISK_LABELS[level] || level;
  const cls = RISK_BADGE_CLASS[level] || RISK_BADGE_CLASS.bajo;
  return (
    <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function FaltantesItemsTable({
  items,
  clientNameById,
  showEmpresa = true,
  saving,
  onOpenExpediente,
  onRequestUpload,
  onSolicitar,
  onPatch,
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
            {showEmpresa ? <th className="px-3 py-2 font-semibold">Empresa</th> : null}
            <th className="px-3 py-2 font-semibold">Faltante</th>
            <th className="px-3 py-2 font-semibold">Periodo</th>
            <th className="px-3 py-2 font-semibold">Detección</th>
            <th className="px-3 py-2 font-semibold">Riesgo</th>
            <th className="px-3 py-2 font-semibold">Estado</th>
            <th className="px-3 py-2 font-semibold text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50/50">
              {showEmpresa ? (
                <td className="px-3 py-2 text-slate-800">
                  {item.client_name || clientNameById[item.client_id] || item.client_id}
                </td>
              ) : null}
              <td className="px-3 py-2">
                <span className="font-medium text-slate-900">{item.category_label || item.label}</span>
                {item.detection_code ? (
                  <span className="block text-[10px] text-slate-500">Detección automática</span>
                ) : null}
              </td>
              <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{formatPeriodLabel(item.period_key)}</td>
              <td className="px-3 py-2 text-xs text-slate-600">
                {item.detection_code
                  ? DETECTION_LABELS[item.detection_code] || item.detection_code
                  : "Manual"}
              </td>
              <td className="px-3 py-2">
                <RiskBadge
                  level={item.risk_level}
                  critical={Boolean(item.critical_finding) || (item.risk_flags || []).some((f) => f === "efos_riesgo" || f === "cfdi_cancelado")}
                />
              </td>
              <td className="px-3 py-2">
                <StatusBadge status={item.status} />
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap justify-end gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-sm"
                    disabled={saving}
                    onClick={() => onOpenExpediente(item)}
                  >
                    <Eye className="h-3 w-3 mr-1" />
                    Ver
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-sm"
                    disabled={saving || item.source_hint !== "manual_upload"}
                    onClick={() => onRequestUpload(item)}
                  >
                    <Upload className="h-3 w-3 mr-1" />
                    Subir
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-sm"
                    disabled={saving || item.status === "requested"}
                    onClick={() => onSolicitar(item)}
                  >
                    <Send className="h-3 w-3 mr-1" />
                    Solicitar
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-sm text-slate-600"
                    disabled={saving}
                    onClick={() => onPatch(item, { status: "waived" })}
                  >
                    No aplica
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs rounded-sm text-emerald-700"
                    disabled={saving}
                    onClick={() => onPatch(item, { status: "complete" })}
                  >
                    OK
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Al expandir una empresa: pestañas Materialidad | Revisión del despacho | Redacción de cartas.
 * El periodo mes/año se controla en el filtro superior del panel.
 */
function CompanyMaterialidadInnerTabs({
  clientId,
  clientName,
  faltantesCount,
  cfdiCount,
  items,
  tableProps,
  periodSelection,
  onPeriodSelectionChange,
}) {
  const [tab, setTab] = useState("expediente");

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      className="space-y-2 px-1"
      data-testid={`materialidad-company-tabs-${clientId}`}
    >
      <TabsList className="rounded-sm h-auto flex flex-wrap gap-1 w-full justify-start">
        <TabsTrigger
          value="expediente"
          className="rounded-sm text-xs"
          data-testid={`materialidad-company-tab-expediente-${clientId}`}
        >
          Materialidad
        </TabsTrigger>
        <TabsTrigger
          value="faltantes"
          className="rounded-sm text-xs"
          data-testid={`materialidad-company-tab-faltantes-${clientId}`}
        >
          {MATERIALIDAD_TAB_FALTANTES_LABEL}
          <span className="ml-1.5 text-[10px] text-slate-500">({faltantesCount})</span>
        </TabsTrigger>
        <TabsTrigger
          value="cartas"
          className="rounded-sm text-xs"
          data-testid={`materialidad-company-tab-cartas-${clientId}`}
        >
          Redacción de cartas
        </TabsTrigger>
      </TabsList>

      <TabsContent value="expediente" className="mt-0" data-testid={`materialidad-company-expediente-${clientId}`}>
        {tab === "expediente" ? (
          <div className="rounded-sm border border-slate-100 bg-slate-50/40 p-2">
            <p className="text-[11px] text-slate-600 m-0 mb-2 px-1">
              {clientName}: elija el proveedor en el selector (periodo de arriba).
              Si faltan proveedores tras subir XML, pulse «Actualizar desde CFDI».
              Luego use las subpestañas CFDI/complementos y Documentos.
            </p>
            <ErrorBoundary inline>
              <MaterialidadContrapartesPanel
                clientId={clientId}
                embeddedInClient
                periodSelection={periodSelection}
                onPeriodSelectionChange={onPeriodSelectionChange}
              />
            </ErrorBoundary>
          </div>
        ) : null}
      </TabsContent>

      <TabsContent value="faltantes" className="mt-0">
        {items.length === 0 ? (
          <p className="text-[11px] text-slate-500 m-0 px-2 py-2">
            {cfdiCount > 0 ? (
              <>
                {cfdiCount} CFDI en el periodo · sin faltantes/señales listadas.
                {" "}Pulse <strong>Analizar riesgos CFDI</strong> para detectar alertas,
                o abra <strong>Materialidad</strong> para proveedores y evidencias.
              </>
            ) : (
              "Sin faltantes y sin CFDI tipo ingreso en este periodo para la empresa."
            )}
          </p>
        ) : (
          <FaltantesItemsTable
            items={items}
            showEmpresa={false}
            {...tableProps}
          />
        )}
      </TabsContent>

      <TabsContent value="cartas" className="mt-0" data-testid={`materialidad-company-cartas-${clientId}`}>
        {tab === "cartas" ? (
          <ErrorBoundary inline>
            <MaterialidadCartasRedaccionPanel clientId={clientId} clientName={clientName} />
          </ErrorBoundary>
        ) : null}
      </TabsContent>
    </Tabs>
  );
}

/**
 * Panel operativo — revisión del despacho (faltantes / soporte documental).
 * Agrupa por auxiliar (asignación Despacho) con filtro Todos/auxiliar y acordeones plegables.
 */
export default function MaterialidadFaltantesPanel({ clientId, clients = [] }) {
  const [periodSelection, setPeriodSelection] = useState(defaultPeriodSelection);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [boardRows, setBoardRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  /** Predeterminado: Todos (no un auxiliar concreto). */
  const [auxFilter, setAuxFilter] = useState(AUX_FILTER_ALL);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState("");
  const [openAuxIds, setOpenAuxIds] = useState([]);
  /** Empresa abierta en modo foco: oculta hermanas y otros responsables hasta cerrarla. */
  const [focusedCompanyId, setFocusedCompanyId] = useState("");
  const uploadInputRef = useRef(null);
  const { isWorkModuleEntitled } = useEntitlements();
  const despachoEntitled = isWorkModuleEntitled("despacho");

  const periodQuery = useMemo(() => buildPeriodQuery(periodSelection), [periodSelection]);
  const periodLabel = summary?.period_label || formatPeriodSelectionLabel(periodSelection);
  const cfdiCountsByClient = summary?.cfdi_counts_by_client || {};

  // Al cambiar periodo, volver al filtro «Todos» (vista completa del equipo).
  useEffect(() => {
    setAuxFilter(AUX_FILTER_ALL);
  }, [periodQuery]);

  // Si el responsable elegido ya no está en el catálogo, volver a Todos.
  useEffect(() => {
    if (!auxFilter || auxFilter === AUX_FILTER_ALL || auxFilter === UNASSIGNED_AUX_ID) return;
    const stillThere = staffRows.some((s) => String(s?.id || "").trim() === auxFilter);
    if (!stillThere && staffRows.length > 0) {
      setAuxFilter(AUX_FILTER_ALL);
    }
  }, [staffRows, auxFilter]);

  // Memoizar mapas: si se recrean en cada render, `grouped` cambia siempre y el efecto
  // de acordeones dispara un bucle setState → ErrorBoundary («Error inesperado»).
  const clientNameById = useMemo(
    () => Object.fromEntries((clients || []).map((c) => [c.id, c.razon_social || c.name || c.id])),
    [clients],
  );
  const clientRfcById = useMemo(
    () => Object.fromEntries((clients || []).map((c) => [c.id, String(c.rfc || "").trim().toUpperCase()])),
    [clients],
  );

  // Importante: NO usar el «cliente de trabajo» global aquí.
  // Si se envía client_id, el backend solo prepara esa empresa y omite
  // ensure_checklists_for_cfdi_clients → el resto queda en 0 faltantes
  // aunque ya tengan CFDI. El filtro de empresa es local (selectedEmpresaId).
  //
  // Carga ligera por defecto (syncCfdi: false): solo lee ítems ya guardados.
  // Incorporar checklist desde CFDI es manual («Actualizar»). Analizar riesgos
  // es otro botón (detección pesada).
  const loadFaltantes = useCallback(async ({ syncCfdi = false } = {}) => {
    setLoading(true);
    try {
      const data = await fetchMaterialidadFaltantes({
        ...periodQuery,
        syncCfdi,
      });
      setSummary(data);
      if (syncCfdi && data?.sync_meta?.error) {
        const detail = String(data.sync_meta.error_detail || "").trim();
        const touched = Number(data.sync_meta.clients_touched || 0);
        const created = Number(data.sync_meta.checklist_items_created || 0);
        if (touched > 0 || created > 0) {
          toast.message(
            detail
              ? `Checklist parcial (${created} nuevo(s)). ${detail}`
              : "Checklist parcial: algunas empresas no se pudieron incorporar.",
          );
        } else {
          toast.error(
            detail
              || "No se pudo incorporar la lista de verificación desde CFDI del periodo. Reinicie el backend (migración de índice) y pulse Actualizar.",
          );
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar la revisión del despacho.");
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [periodQuery]);

  const loadAssignmentBoard = useCallback(async () => {
    setBoardLoading(true);
    try {
      const [rows, staff] = await Promise.all([
        fiscalAssignmentBoardApi.listBoard().catch(() => []),
        despachoStaffApi.list().catch(() => []),
      ]);
      setBoardRows(Array.isArray(rows) ? rows : []);
      setStaffRows(Array.isArray(staff) ? staff : []);
    } catch {
      setBoardRows([]);
      setStaffRows([]);
    } finally {
      setBoardLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFaltantes({ syncCfdi: false });
  }, [loadFaltantes]);

  useEffect(() => {
    loadAssignmentBoard();
  }, [loadAssignmentBoard]);

  const items = useMemo(() => summary?.items || [], [summary]);
  const staffOptions = useMemo(() => listAssignableStaff(staffRows), [staffRows]);

  const clientAuxMap = useMemo(() => buildClientAuxiliaresMap(boardRows), [boardRows]);
  const grouped = useMemo(
    () => groupFaltantesByAuxiliar(items, clientAuxMap, clientNameById, boardRows, staffOptions),
    [items, clientAuxMap, clientNameById, boardRows, staffOptions],
  );
  /** Formato ClientWorkPickerList ({ id, razon_social, rfc }). */
  const empresaPickerClients = useMemo(() => {
    const byId = new Map();
    for (const c of clients || []) {
      const id = String(c.id || "").trim();
      if (!id) continue;
      byId.set(id, {
        id,
        razon_social: c.razon_social || c.name || id,
        nombre_comercial: c.nombre_comercial || "",
        rfc: String(c.rfc || "").trim().toUpperCase(),
      });
    }
    for (const g of grouped) {
      for (const company of g.companies || []) {
        const id = String(company.clientId || "").trim();
        if (!id || byId.has(id)) continue;
        byId.set(id, {
          id,
          razon_social: company.clientName || id,
          rfc: clientRfcById[id] || "",
        });
      }
    }
    return [...byId.values()].sort((a, b) =>
      String(a.razon_social).localeCompare(String(b.razon_social), "es", { sensitivity: "base" }),
    );
  }, [clients, grouped, clientRfcById]);

  const selectedEmpresa = useMemo(
    () => empresaPickerClients.find((o) => o.id === selectedEmpresaId) || null,
    [empresaPickerClients, selectedEmpresaId],
  );

  const visibleGroups = useMemo(
    () => filterAuxiliarGroups(grouped, auxFilter, "", selectedEmpresaId, clientRfcById),
    [grouped, auxFilter, selectedEmpresaId, clientRfcById],
  );

  const visiblePendingTotal = useMemo(
    () => visibleGroups.reduce((n, g) => n + (Number(g.total) || 0), 0),
    [visibleGroups],
  );
  const hasEmpresaOrAuxFilter = Boolean(
    selectedEmpresaId
    || (auxFilter && auxFilter !== AUX_FILTER_ALL),
  );
  const pendingHeaderLabel = hasEmpresaOrAuxFilter
    ? `${visiblePendingTotal} pendiente(s) en vista`
    : `${summary?.total ?? 0} pendiente(s)`;

  // Acordeones de auxiliar: cerrados por defecto; con filtro de empresa, abre coincidencias.
  // Comparar por clave evita setState redundantes (y bucles si la identidad de arrays cambia).
  const openAuxIdsKey = useMemo(() => {
    if (!selectedEmpresaId) return "";
    return visibleGroups.map((g) => g.auxId).join("\0");
  }, [selectedEmpresaId, visibleGroups]);

  useEffect(() => {
    if (!openAuxIdsKey) {
      setOpenAuxIds((prev) => (prev.length ? [] : prev));
      return;
    }
    const next = openAuxIdsKey.split("\0").filter(Boolean);
    setOpenAuxIds((prev) => {
      if (prev.length === next.length && prev.every((id, i) => id === next[i])) return prev;
      return next;
    });
  }, [openAuxIdsKey]);

  // Si el picker acota a una empresa, entrar en modo foco (sin hermanas visibles).
  useEffect(() => {
    if (selectedEmpresaId) setFocusedCompanyId(selectedEmpresaId);
  }, [selectedEmpresaId]);

  const focusedAuxId = useMemo(() => {
    if (!focusedCompanyId) return "";
    for (const g of visibleGroups) {
      if ((g.companies || []).some((c) => c.clientId === focusedCompanyId)) {
        return g.auxId;
      }
    }
    return "";
  }, [focusedCompanyId, visibleGroups]);

  const focusModeGroups = useMemo(() => {
    if (!focusedCompanyId || !focusedAuxId) return visibleGroups;
    return visibleGroups
      .filter((g) => g.auxId === focusedAuxId)
      .map((g) => {
        const companies = (g.companies || []).filter((c) => c.clientId === focusedCompanyId);
        const total = companies.reduce((n, c) => n + Number(c.total || c.items?.length || 0), 0);
        return { ...g, companies, total };
      })
      .filter((g) => (g.companies || []).length > 0);
  }, [visibleGroups, focusedCompanyId, focusedAuxId]);

  const closeCompanyFocus = useCallback(() => {
    setFocusedCompanyId((current) => {
      if (current) {
        setSelectedEmpresaId((sel) => (sel === current ? "" : sel));
      }
      return "";
    });
  }, []);

  const updateItemLocally = (updated) => {
    if (!updated?.id) return;
    setSummary((prev) => {
      if (!prev) return prev;
      const nextItems = prev.items.map((row) => (row.id === updated.id ? { ...row, ...updated } : row));
      const faltanteStatuses = new Set(["pending", "requested", "rejected", "in_review"]);
      const filtered = nextItems.filter((row) => faltanteStatuses.has(row.status));
      return { ...prev, items: filtered, total: filtered.length };
    });
    setSelectedItem((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const handlePatch = async (item, patch, { quiet = false } = {}) => {
    setSaving(true);
    try {
      const updated = await patchMaterialidadItem(item.id, patch);
      updateItemLocally(updated);
      if (!quiet) toast.success("Estado actualizado.");
      return updated;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el ítem.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (item, file) => {
    setSaving(true);
    try {
      const updated = await uploadMaterialidadEvidence(item.id, file);
      updateItemLocally(updated);
      toast.success("Evidencia cargada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el archivo.");
    } finally {
      setSaving(false);
    }
  };

  const handleSolicitar = async (item) => {
    const clientName = item.client_name || clientNameById[item.client_id] || "empresa";
    const title = `Materialidad: ${item.category_label || item.label} — ${clientName} (${formatPeriodLabel(item.period_key)})`;

    if (despachoEntitled) {
      setSaving(true);
      try {
        const task = await despachoFiscalInfoApi.createTask({
          title,
          task_domain: "ad_hoc",
          template_key: "otro",
          fiscal_client_id: item.client_id,
          initial_message: title,
          send_initial_reminder: false,
        });
        const taskId = task?.id || task?.task_id;
        if (taskId) {
          const updated = await linkMaterialidadTask(item.id, taskId);
          updateItemLocally(updated);
          toast.success("Tarea creada y vinculada.");
          return;
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status !== 403 && status !== 404) {
          toast.error(err?.response?.data?.detail || "No se pudo crear la tarea en Despacho.");
          setSaving(false);
          return;
        }
      } finally {
        setSaving(false);
      }
    }

    await handlePatch(item, { status: "requested" });
    if (!despachoEntitled) {
      toast.message("Marcado como solicitado. Active Gestión Despacho para crear tareas automáticas.");
    }
  };

  const handleSyncCfdi = async () => {
    setSaving(true);
    try {
      const targetId = selectedEmpresaId || "";
      if (targetId) {
        const result = await syncMaterialidadCfdi(targetId, periodQuery, { force: true });
        toast.success(
          `Detección CFDI (empresa filtrada): ${result.created || 0} nuevo(s), ${result.updated || 0} actualizado(s).`,
        );
      } else {
        const result = await syncMaterialidadCfdiBatch(periodQuery, { force: true });
        toast.success(
          `Detección en ${result.clients_scanned || 0} empresa(s) con CFDI: `
          + `${result.created || 0} nuevo(s), ${result.updated || 0} actualizado(s).`,
        );
        if (result.clients_with_cfdi === 0) {
          toast.message("No hay CFDI tipo ingreso en el periodo seleccionado.");
        }
      }
      await loadFaltantes({ syncCfdi: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo sincronizar desde CFDI.");
    } finally {
      setSaving(false);
    }
  };

  const cartaClientId = selectedEmpresaId || clientId || "";

  const openExpediente = (item) => {
    setSelectedItem(item);
    setDrawerOpen(true);
  };

  const requestUpload = (item) => {
    setSelectedItem(item);
    uploadInputRef.current?.click();
  };

  const tableProps = {
    clientNameById,
    saving,
    onOpenExpediente: openExpediente,
    onRequestUpload: requestUpload,
    onSolicitar: handleSolicitar,
    onPatch: handlePatch,
  };

  return (
    <div className="space-y-4" data-testid="materialidad-faltantes-panel">
      {summary?.sync_meta?.scope && summary.sync_meta.scope.confronta_hint === "sin_excel" ? (
        <div className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Sin contabilidad cargada para este periodo: la detección aplica a todos los CFDI del Visor.
          Cargue Excel o ejecute confronta para filtrar solo lo contabilizado.
        </div>
      ) : null}

      <div className="rounded-md border border-slate-200 bg-white px-3 py-2.5 shadow-sm space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <MaterialidadPeriodFilter
            idPrefix="materialidad-faltantes"
            value={periodSelection}
            onChange={setPeriodSelection}
            compact
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2">
          <div className="flex-1 min-w-[140px] text-xs text-slate-600">
            {selectedEmpresaId
              ? `Filtro empresa · ${periodLabel}`
              : `Todas las empresas · ${periodLabel}`}
            <span className="block text-[10px] text-slate-500">
              {selectedEmpresaId
                ? "Carga ligera al abrir. Actualizar incorpora la lista de verificación; Analizar detecta riesgos CFDI."
                : "Al entrar solo se lee el expediente guardado. Use Actualizar para incorporar la lista de verificación desde CFDI; Analizar detecta riesgos."}
            </span>
            {summary?.sync_meta?.clients_with_cfdi != null ? (
              <span className="block text-[10px] text-[#0F2D5C]/80 mt-0.5" data-testid="materialidad-cfdi-clients-meta">
                {summary.sync_meta.clients_with_cfdi} empresa(s) con CFDI tipo ingreso en el periodo
                {summary.sync_meta.checklist_items_created
                  ? ` · ${summary.sync_meta.checklist_items_created} ítem(s) nuevos de la lista de verificación`
                  : ""}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm shrink-0 h-8"
            disabled={loading || saving}
            onClick={handleSyncCfdi}
            data-testid="materialidad-sync-cfdi"
            title="Detecta señales de riesgo en CFDI del periodo; no sustituye Actualizar"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ScanSearch className="h-4 w-4 mr-1" />}
            Analizar riesgos CFDI
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm shrink-0 h-8"
            disabled={loading}
            onClick={() => {
              loadFaltantes({ syncCfdi: true });
              loadAssignmentBoard();
            }}
            data-testid="materialidad-refresh-faltantes"
            title="Incorpora la lista de verificación de empresas con CFDI del periodo y refresca el listado"
          >
            {loading || boardLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Actualizar
          </Button>
          <Button type="button" variant="outline" size="sm" className="rounded-sm shrink-0 h-8" asChild>
            <a
              href={materialidadFaltantesPdfUrl({
                ...periodQuery,
                clientId: selectedEmpresaId || undefined,
              })}
              download
              data-testid="materialidad-download-faltantes-pdf"
            >
              <Download className="h-4 w-4 mr-1" />
              PDF
            </a>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm shrink-0 h-8"
            disabled={!cartaClientId}
            asChild={Boolean(cartaClientId)}
            data-testid="materialidad-download-carta-cliente"
            title={
              cartaClientId
                ? undefined
                : "Elija una empresa en el filtro o en el selector de cliente de trabajo"
            }
          >
            {cartaClientId ? (
              <a href={materialidadCartaClientePdfUrl({ ...periodQuery, clientId: cartaClientId })} download>
                <FileText className="h-4 w-4 mr-1" />
                Carta PDF
              </a>
            ) : (
              <span>
                <FileText className="h-4 w-4 mr-1 inline" />
                Carta PDF
              </span>
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm shrink-0 h-8"
            disabled={!cartaClientId}
            asChild={Boolean(cartaClientId)}
            data-testid="materialidad-download-carta-cliente-excel"
          >
            {cartaClientId ? (
              <a href={materialidadCartaClienteXlsxUrl({ ...periodQuery, clientId: cartaClientId })} download>
                Excel
              </a>
            ) : (
              <span>Excel</span>
            )}
          </Button>
        </div>
      </div>

      <div
        className="flex flex-col gap-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm"
        data-testid="materialidad-auxiliar-filters"
      >
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700 max-w-xs">
          Asignado a
          <select
            className="h-9 rounded-sm border border-slate-200 bg-white px-2 text-sm font-normal text-slate-900"
            value={auxFilter || AUX_FILTER_ALL}
            autoComplete="off"
            onChange={(e) => {
              const next = e.target.value || AUX_FILTER_ALL;
              setAuxFilter(next);
              // Al volver a Todos, salir del foco de una sola empresa.
              if (next === AUX_FILTER_ALL) {
                setFocusedCompanyId("");
              }
            }}
            data-testid="materialidad-auxiliar-select"
          >
            <option value={AUX_FILTER_ALL}>Todos</option>
            {staffOptions.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.name}
                {staff.despacho_role ? ` (${staffRoleLabel(staff.despacho_role)})` : ""}
              </option>
            ))}
            {grouped.some((g) => g.auxId === UNASSIGNED_AUX_ID) ? (
              <option value={UNASSIGNED_AUX_ID}>Sin responsable asignado</option>
            ) : null}
          </select>
        </label>

        {/* Búsqueda siempre visible: se puede teclear nombre/RFC sin abrir paneles */}
        <section
          className="rounded-sm border border-slate-200 bg-white px-3 py-3 space-y-2"
          data-testid="materialidad-empresa-picker"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold m-0">
                Buscar empresa
              </p>
              {selectedEmpresa ? (
                <p className="text-sm text-[#0F2D5C] m-0 mt-0.5 truncate">
                  <span className="font-semibold">{selectedEmpresa.razon_social || "Sin razón social"}</span>
                  {selectedEmpresa.rfc ? (
                    <span className="ml-2 font-mono text-xs text-slate-600">{selectedEmpresa.rfc}</span>
                  ) : null}
                  <span className="ml-2 text-[9px] font-bold uppercase tracking-wide text-[#3DA873]">Filtro</span>
                </p>
              ) : (
                <p className="text-xs text-slate-500 m-0 mt-0.5">
                  Escriba el nombre o RFC para filtrar el listado
                </p>
              )}
            </div>
            {selectedEmpresaId ? (
              <button
                type="button"
                className="text-xs font-medium text-[#0F2D5C] underline shrink-0"
                onClick={() => setSelectedEmpresaId("")}
                data-testid="materialidad-empresa-clear"
              >
                Quitar filtro
              </button>
            ) : null}
          </div>
          {empresaPickerClients.length > 0 ? (
            <div data-testid="materialidad-empresa-search">
              <ClientWorkPickerList
                clients={empresaPickerClients}
                clientId={selectedEmpresaId}
                autoFocusSearch={false}
                requireSearch
                onSelect={(id) => setSelectedEmpresaId(id)}
              />
            </div>
          ) : (
            <p className="text-sm text-slate-500 m-0">Sin empresas para filtrar.</p>
          )}
        </section>

        <p className="text-[11px] text-slate-500 m-0">
          Teclee el nombre o RFC arriba, o use «Asignado a» (Gestión Despacho).
          {boardLoading ? " Cargando equipo…" : null}
        </p>
      </div>

      {summary?.sync_meta?.created > 0 || summary?.sync_meta?.updated > 0 ? (
        <p className="text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-3 py-2 m-0">
          Detección automática: {summary.sync_meta.created || 0} nuevo(s), {summary.sync_meta.updated || 0} actualizado(s).
        </p>
      ) : null}
      {summary?.sync_meta?.checklist_items_created > 0 ? (
        <p className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 m-0">
          Expediente: se incorporaron {summary.sync_meta.checklist_items_created} ítem(s) de la lista de verificación
          para {summary.sync_meta.clients_touched || 0} empresa(s) con CFDI ya cargados en el periodo.
        </p>
      ) : null}

      <section
        className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden"
        aria-label="Revisión del despacho — materialidad"
        data-testid="materialidad-faltantes-table-section"
      >
        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-[#0F2D5C]" />
          <h2 className="text-sm font-bold text-[#0F2D5C] m-0">{MATERIALIDAD_TAB_FALTANTES_LABEL}</h2>
          <span className="text-xs text-slate-500 ml-auto" data-testid="materialidad-faltantes-pending-count">
            {pendingHeaderLabel}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Cargando revisión del despacho…
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="p-6 text-sm text-slate-600 space-y-2">
            {items.length === 0 && staffOptions.length === 0 ? (
              <>
                <p className="m-0">No hay pendientes de revisión ni usuarios del equipo para agrupar.</p>
                {clientId ? (
                  <p className="text-xs text-slate-500 m-0">
                    Guarde la configuración en la pestaña Configuración y vuelva aquí; la lista de verificación se crea al cargar faltantes.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="m-0">Ningún responsable o empresa coincide con la búsqueda o el filtro seleccionado.</p>
            )}
          </div>
        ) : (
          <Accordion
            type="multiple"
            value={focusedCompanyId && focusedAuxId ? [focusedAuxId] : openAuxIds}
            onValueChange={(next) => {
              // En modo foco no se puede cerrar el responsable (solo la empresa).
              if (focusedCompanyId && focusedAuxId) {
                setOpenAuxIds([focusedAuxId]);
                return;
              }
              setOpenAuxIds(next);
            }}
            className="divide-y divide-slate-100"
            data-testid="materialidad-auxiliar-accordion"
          >
            {focusModeGroups.map((group) => {
              const roleLbl = staffRoleLabel(group.despacho_role);
              const companyValue = focusedCompanyId
                && (group.companies || []).some((c) => c.clientId === focusedCompanyId)
                ? focusedCompanyId
                : "";
              return (
              <AccordionItem key={group.auxId} value={group.auxId} className="border-0 px-3">
                <AccordionTrigger className="text-xs font-semibold text-[#0F2D5C] hover:no-underline py-3">
                  <span className="flex flex-1 items-center justify-between gap-2 pr-2 min-w-0">
                    <span className="truncate text-left">
                      {group.auxName}
                      {roleLbl ? (
                        <span className="ml-2 text-[10px] font-normal text-slate-500">({roleLbl})</span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-2 shrink-0 text-[10px] font-normal text-slate-500">
                      <span>{group.companies.length} empresa(s)</span>
                      <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-semibold text-[#0F2D5C]">
                        {group.total}
                      </span>
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  {group.companies.length === 0 ? (
                    <p className="text-[11px] text-slate-500 m-0 px-1 py-2">
                      Sin empresas asignadas a este responsable.
                    </p>
                  ) : (
                  <>
                  {focusedCompanyId && companyValue ? (
                    <div
                      className="mb-2 flex flex-wrap items-center gap-2 px-1"
                      data-testid="materialidad-empresa-focus-bar"
                    >
                      <p className="m-0 text-[11px] text-slate-600 flex-1 min-w-[12rem]">
                        Trabajando solo en esta empresa. Cierre la pestaña para volver al listado.
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-sm text-xs"
                        onClick={closeCompanyFocus}
                        data-testid="materialidad-empresa-focus-close"
                      >
                        Cerrar y ver listado
                      </Button>
                    </div>
                  ) : null}
                  <Accordion
                    type="single"
                    collapsible
                    value={companyValue}
                    onValueChange={(next) => {
                      if (next) {
                        setFocusedCompanyId(next);
                        setOpenAuxIds((prev) => (
                          prev.includes(group.auxId) ? prev : [...prev, group.auxId]
                        ));
                        return;
                      }
                      closeCompanyFocus();
                    }}
                    className="rounded-sm border border-slate-200 bg-white"
                    data-testid={`materialidad-empresas-${group.auxId}`}
                  >
                    {sortCompaniesByActivity(group.companies, cfdiCountsByClient).map((company) => {
                      const cfdiN = Number(cfdiCountsByClient[company.clientId] || 0);
                      return (
                      <AccordionItem
                        key={company.clientId}
                        value={company.clientId}
                        className="border-slate-200 px-2"
                        data-testid={`materialidad-empresa-item-${company.clientId}`}
                      >
                        <AccordionTrigger className="text-xs font-semibold text-slate-800 hover:no-underline py-2.5">
                          <span className="flex flex-1 items-center justify-between gap-2 pr-2 min-w-0">
                            <span className="truncate text-left">{company.clientName}</span>
                            <span className="inline-flex shrink-0 items-center gap-2 text-[10px] font-normal text-slate-500">
                              {cfdiN > 0 ? (
                                <span className="text-[#0F2D5C]/80" data-testid={`materialidad-cfdi-count-${company.clientId}`}>
                                  {cfdiN} CFDI
                                </span>
                              ) : (
                                <span className="text-slate-400">Sin CFDI</span>
                              )}
                              <span>{company.total} faltante(s)</span>
                            </span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-2">
                          <CompanyMaterialidadInnerTabs
                            clientId={company.clientId}
                            clientName={company.clientName}
                            faltantesCount={company.total}
                            cfdiCount={Number(cfdiCountsByClient[company.clientId] || 0)}
                            items={company.items}
                            tableProps={tableProps}
                            periodSelection={periodSelection}
                            onPeriodSelectionChange={setPeriodSelection}
                          />
                        </AccordionContent>
                      </AccordionItem>
                      );
                    })}
                  </Accordion>
                  </>
                  )}
                </AccordionContent>
              </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </section>

      <input
        ref={uploadInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && selectedItem) handleUpload(selectedItem, file);
          e.target.value = "";
        }}
      />

      <MaterialidadExpedienteDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        item={selectedItem}
        clientName={selectedItem ? (selectedItem.client_name || clientNameById[selectedItem.client_id]) : ""}
        saving={saving}
        uploadInputRef={uploadInputRef}
        onSaveNotes={(item, notes) => handlePatch(item, { notes }, { quiet: true })}
        onSaveBusinessReason={(item, patch) => handlePatch(item, patch, { quiet: true })}
        onUpload={handleUpload}
      />
    </div>
  );
}
